/**
 * Small React mounting and navigation helpers for the strategic UI (M4 plan I6). A mount owns one element it
 * appends to its parent and renders synchronously (`flushSync`), so a host that reads the buttons right after
 * (M2's station screen focus model) sees them. `navigateButtons` moves the DOM focus through an overlay's buttons
 * for the keyboard and the pad (the pause menu's `MenuInput`s). UI events only.
 */
import type { ReactNode } from 'react'
import { flushSync } from 'react-dom'
import { createRoot, type Root } from 'react-dom/client'

export interface ReactMount {
  readonly el: HTMLElement
  render(node: ReactNode): void
  unmount(): void
}

/**
 * Mounts a full-screen layer in a new `div.className` inserted right after `anchor` (`#overlay`), like the map and
 * station layers: `#overlay` is `hidden` in flight, so a layer inside it never shows unless the pause menu is up
 * (D-TBD, C21). Later DOM order draws it above the pause menu when opened from a menu tab. A detached anchor (unit
 * tests) gets the layer appended instead.
 */
export function mountLayerAfter(anchor: HTMLElement, className: string): ReactMount {
  const el = anchor.ownerDocument.createElement('div')
  el.className = className
  if (anchor.parentNode === null) anchor.appendChild(el)
  else anchor.after(el)
  const inner = mountReact(el, className, false)
  return {
    el,
    render: (node: ReactNode): void => inner.render(node),
    unmount(): void {
      inner.unmount()
      el.remove()
    },
  }
}

/** Mounts a React root in a new `div.className` appended to `parent` (or into `parent` itself when `own` is false). */
export function mountReact(parent: HTMLElement, className: string, own = true): ReactMount {
  const el = own ? parent.ownerDocument.createElement('div') : parent
  if (own) {
    el.className = className
    parent.appendChild(el)
  }
  let root: Root | null = createRoot(el)
  return {
    el,
    render(node: ReactNode): void {
      const r = root
      if (r === null) return
      flushSync(() => r.render(node))
    },
    unmount(): void {
      const r = root
      if (r === null) return
      root = null
      r.unmount()
      if (own) el.remove()
    },
  }
}

export type NavInput = 'up' | 'down' | 'left' | 'right' | 'activate' | 'back' | 'start'

/**
 * Moves the focus through the enabled buttons of `root` (up/down, wrapping); left/right go to the root's own
 * keydown handler (the Wire filter); activate clicks the focused button. Returns true when it handled the input.
 */
export function navigateButtons(root: HTMLElement, input: NavInput): boolean {
  const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>('button:not([disabled])'))
  if (buttons.length === 0) return false
  const doc = root.ownerDocument
  const active = doc.activeElement
  const i = buttons.findIndex((b) => b === active)
  switch (input) {
    case 'up':
    case 'down': {
      const n = buttons.length
      const next = i < 0 ? 0 : (i + (input === 'up' ? n - 1 : 1)) % n
      buttons[next].focus()
      return true
    }
    case 'left':
    case 'right': {
      const target = i >= 0 ? buttons[i] : root
      target.dispatchEvent(new KeyboardEvent('keydown', { code: input === 'left' ? 'ArrowLeft' : 'ArrowRight', bubbles: true }))
      return true
    }
    case 'activate':
      ;(i >= 0 ? buttons[i] : buttons[0]).click()
      return true
    case 'back':
    case 'start':
      return false
  }
}
